import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { today } from '../api/calendar'
import DeclareOutDay, { refreshAfterDeclaring } from './DeclareOutDay'
import {
  addTimeOff,
  blockLabel,
  fetchMyTimeOff,
  isPast,
  removeTimeOff,
  type TimeOffBlock,
  type TimeOffPrivate,
} from '../api/timeOff'
import InfoTip from './InfoTip'

/**
 * Dates a teacher is away (D-681).
 *
 * **Beside her regular hours, and deliberately the opposite of them.** Hours are
 * a rule about a term that the studio agrees to; this is a fact about some dates
 * that nobody approves. It is live the moment she saves it.
 *
 * **The hard one.** Outside her published hours a parent is turned away and a
 * colleague is only warned, because a teacher who has privately arranged cover
 * must stay askable (D-675). Away is away: no family may book her and no
 * colleague may ask her to cover.
 *
 * **Silent for a parent**, who has no staff row and nothing to set.
 */
/**
 * One line for a private lesson inside a block: <i>Private with Ava Cotton, Tue
 * 9 Mar 7:00pm</i>. UTC-anchored dates, as `blockLabel` is (D-68, D-485).
 */
export function privateLabel(lesson: TimeOffPrivate): string {
  const day = new Date(`${lesson.date}T00:00:00Z`).toLocaleDateString('en-GB', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    timeZone: 'UTC',
  })

  const [h, m] = lesson.startTime.split(':').map(Number)
  const hour = ((h + 11) % 12) + 1
  const time = `${hour}:${String(m).padStart(2, '0')}${h < 12 ? 'am' : 'pm'}`

  const who = lesson.dancerNames ? `Private with ${lesson.dancerNames}` : 'Private lesson'

  return `${who}, ${day} ${time}`
}

export default function TimeOffPanel({
  isStaff,
  isKiosk,
}: {
  isStaff: boolean
  /** A wall tablet is nobody in particular and sets nothing (D-233). */
  isKiosk: boolean
}) {
  const enabled = isStaff && !isKiosk

  const { data, isPending, isError } = useQuery({
    queryKey: ['my-time-off'],
    queryFn: fetchMyTimeOff,
    enabled,
  })

  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)

  /*
   * The day she has opened the absence dialog on, or nothing (D-683).
   *
   * **The same dialog her own portal opens**, not a second one built here. It
   * takes a date and fetches the rest itself, which is what makes reusing it a
   * two-line change rather than a rebuild — and a second implementation of
   * *declare a day out* is the defect this codebase names first.
   */
  const [sortingDay, setSortingDay] = useState<string | null>(null)

  const queryClient = useQueryClient()
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['my-time-off'] })

  const add = useMutation({
    mutationFn: () => addTimeOff(from, to || from, note.trim() || null),
    onMutate: () => setError(null),
    onSuccess: () => {
      setFrom('')
      setTo('')
      setNote('')
      refresh()
    },
    onError: (e: Error) => setError(e.message),
  })

  const remove = useMutation({
    mutationFn: removeTimeOff,
    onMutate: () => setError(null),
    onSuccess: refresh,
    onError: (e: Error) => setError(e.message),
  })

  if (!enabled) return null

  const field =
    'rounded-lg border border-line-2 bg-surface-2 px-2 py-1.5 text-[13px] outline-none focus:border-accent focus:bg-surface'

  const blocks = data ?? []
  const now = today()

  /*
   * <b>A single date is a block of one.</b> Leaving the second box empty is the
   * commonest case there is - one Friday - and making her type the same date
   * twice to express it is the kind of friction that stops people recording
   * holidays at all. The server is told `to = from`, so nothing downstream has
   * to know the box was blank.
   */
  const ready = from !== '' && (to === '' || to >= from)

  return (
    <section className="rounded-2xl border border-line bg-surface px-4 py-4">
      <div className="flex flex-wrap items-center gap-1">
        <h2 className="text-[11px] font-bold tracking-[0.11em] text-ink-3 uppercase">Time off</h2>
        <InfoTip label="More about time off">
          Families can&rsquo;t ask you for a private on these dates, and nobody can ask you to cover
          a class. It takes effect straight away &mdash; nobody has to approve it.
        </InfoTip>
      </div>

      {/*
        <b>Both halves, now that both are built.</b> This deliberately claimed
        only the parent half while the cover refusal was outstanding (D-681): a
        screen promising a protection the app did not have, to the one person
        who would rely on it, on a host testers use. D-682 closed it at every
        door a colleague can be asked through &mdash; the ask itself included,
        which is the one a URL cannot walk past &mdash; so the sentence widens
        here, in the commit that made it true.
      */}
      <p className="mt-1 text-[13px] text-ink-2">Dates you&rsquo;re away. No privates or cover requests then.</p>

      {isPending ? (
        <p className="mt-3 text-[13px] text-ink-3">Loading...</p>
      ) : isError ? (
        /*
         * <b>A failed read is never a statement about her data</b> (D-209).
         * Without this the panel falls through to the empty state and tells her
         * she has no time off recorded, with a reassurance attached — and the
         * commonest cause is the ~75 seconds after a deploy when every
         * database-backed endpoint refuses (D-33), with the client's one retry
         * spent inside a second. Her natural response is to book it again.
         */
        <p
          role="alert"
          className="mt-3 rounded-lg border border-line bg-surface-2 px-3 py-2.5 text-[12.5px] text-ink-2"
        >
          Could not load your time off just now. Anything you have recorded is still there &mdash;
          try again shortly.
        </p>
      ) : (
        <>
          {blocks.length > 0 && (
            <ul className="mt-3 flex flex-col gap-1.5">
              {blocks.map((block: TimeOffBlock) => (
                <li
                  key={block.id}
                  className="flex flex-wrap items-center gap-2 rounded-lg border border-line-2 bg-surface-2 px-3 py-2"
                >
                  <span className="text-[13px] font-semibold text-ink">
                    {blockLabel(block.from, block.to)}
                  </span>

                  {block.note && <span className="text-[12.5px] text-ink-2">{block.note}</span>}

                  {/*
                    A block that has already finished is shown and not offered
                    for removal - there is nothing to take back, and a button
                    that changes nothing is worse than no button (D-333).
                  */}
                  {isPast(block, now) ? (
                    <span className="ml-auto text-[11.5px] text-ink-3">over</span>
                  ) : (
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => remove.mutate(block.id)}
                      aria-label={`Remove time off ${blockLabel(block.from, block.to)}`}
                      className="ml-auto min-h-[36px] px-1.5 text-[12px] font-semibold text-ink-3 underline underline-offset-2 hover:text-crit disabled:opacity-60"
                    >
                      Remove
                    </button>
                  )}

                  {/*
                    **What she teaches then, counted and not arranged** (D-683).

                    Booking a holiday raises nothing: no cover request, no
                    cancellation, no substitute. The owner's rule, and the
                    reason is that she has not decided who takes her Tuesdays
                    just because she has booked a flight. So this is a sentence
                    and a way in, never an action taken for her.

                    **It opens on the first day that still has something**, not
                    on the first day of the block - a Monday she has already
                    dealt with would open an empty dialog, which reads as a
                    broken button (D-365).

                    **Not offered on a block that is over.** There is nothing to
                    arrange for last month, and a control that cannot help is
                    worse than none (D-333).
                  */}
                  {!isPast(block, now) && block.unarranged > 0 && block.firstUnarranged && (
                    <div className="flex w-full flex-wrap items-center gap-2 border-t border-line-2 pt-2">
                      <span className="text-[12.5px] text-ink-2">
                        {block.unarranged === 1
                          ? '1 class here still needs cover or cancelling.'
                          : `${block.unarranged} classes here still need cover or cancelling.`}
                      </span>

                      <button
                        type="button"
                        onClick={() => setSortingDay(block.firstUnarranged)}
                        className="ml-auto min-h-[36px] rounded-lg border border-line-2 bg-surface px-2.5 text-[12px] font-semibold text-accent hover:brightness-105"
                      >
                        {block.unarranged === 1 ? 'Sort it out' : 'Sort them out'}
                      </button>
                    </div>
                  )}

                  {/*
                    **The private lessons she gives that week** (D-730). A private
                    is hers through its booking, never a class to declare against,
                    so each one points at its day, where the booking can be moved
                    or cancelled - and it stays here until she does.
                  */}
                  {!isPast(block, now) && block.privates.length > 0 && (
                    <ul className="flex w-full flex-col gap-1 border-t border-line-2 pt-2">
                      {block.privates.map((lesson: TimeOffPrivate) => (
                        <li
                          key={lesson.bookingId}
                          className="flex flex-wrap items-center gap-2 text-[12.5px] text-ink-2"
                        >
                          <span>{privateLabel(lesson)}</span>
                          <Link
                            to={`/calendar/day/${lesson.date}`}
                            className="ml-auto min-h-[36px] px-1.5 text-[12px] font-semibold text-accent underline underline-offset-2"
                          >
                            Move or cancel
                          </Link>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}

          {blocks.length === 0 && (
            <p className="mt-3 text-[12.5px] text-ink-3">
              No time off recorded.
            </p>
          )}

          <div className="mt-3 flex flex-wrap items-end gap-2">
            <label className="flex flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-2">First day away</span>
              <input
                type="date"
                value={from}
                onChange={(e) => {
                  setError(null)
                  setFrom(e.target.value)
                }}
                className={field}
              />
            </label>

            <label className="flex flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-2">Last day (optional)</span>
              <input
                type="date"
                value={to}
                min={from || undefined}
                onChange={(e) => {
                  setError(null)
                  setTo(e.target.value)
                }}
                className={field}
              />
            </label>

            <label className="flex min-w-0 flex-1 flex-col gap-1">
              <span className="text-[11.5px] font-semibold text-ink-2">Note (optional)</span>
              <input
                type="text"
                value={note}
                maxLength={200}
                placeholder="Only the studio sees this"
                onChange={(e) => {
                  setError(null)
                  setNote(e.target.value)
                }}
                className={field}
              />
            </label>

            <button
              type="button"
              disabled={!ready || add.isPending}
              onClick={() => add.mutate()}
              className="min-h-[36px] rounded-lg bg-accent px-3 py-1.5 text-[13px] font-semibold text-on-accent hover:brightness-110 disabled:opacity-60"
            >
              {add.isPending ? 'Adding...' : 'Add'}
            </button>
          </div>

          {error && (
            <p
              role="alert"
              className="mt-3 rounded-lg border border-crit bg-crit-bg px-3 py-2 text-[12.5px] text-crit"
            >
              {error}
            </p>
          )}

          {sortingDay && (
            <DeclareOutDay
              initialDate={sortingDay}
              onClose={() => setSortingDay(null)}
              /*
               * The shared list, not a copy of it. The count on the block she
               * is looking at is recounted server-side on every read, so it
               * falls only if `my-time-off` is dropped - and a copy that had
               * the key here and nowhere else was exactly the bug: sort a
               * class out from her portal, walk back, read the old number.
               */
              onDone={() => refreshAfterDeclaring(queryClient)}
            />
          )}
        </>
      )}
    </section>
  )
}
